import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';

export interface AuthUserInfo {
  id: string;
  username: string;
  name: string;
  email: string | null;
  role: string;
}

interface StoredSession {
  accessToken: string;
  refreshToken: string;
  user: AuthUserInfo;
}

interface LoginResponse extends StoredSession {
  capability: Record<string, unknown>;
}

interface AuthState {
  authenticated: boolean;
  user: AuthUserInfo | null;
  /** Current access token (null in dev bypass — backend bypass accepts tokenless calls). */
  getToken: () => string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  /** Exchange the refresh token; resolves the new access token or null. */
  refresh: () => Promise<string | null>;
}

const AuthContext = createContext<AuthState | null>(null);

const STORAGE_KEY = 'dikonek-ops-auth';

/**
 * Dev-only: skip login entirely and run as a stub user. Pair with the backend's
 * AUTH_DEV_BYPASS so API calls (sent without a token) are accepted.
 * Set VITE_AUTH_DEV_BYPASS=true in .env.local. Never enable in production.
 */
const DEV_BYPASS = import.meta.env.VITE_AUTH_DEV_BYPASS === 'true';

function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed.accessToken || !parsed.refreshToken || !parsed.user) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSession(session: StoredSession | null): void {
  if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  else localStorage.removeItem(STORAGE_KEY);
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = `login failed (${res.status})`;
    try {
      const json = (await res.json()) as { message?: string | string[] };
      const m = Array.isArray(json.message) ? json.message.join(', ') : json.message;
      if (m) message = m;
    } catch {
      /* no JSON body */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<StoredSession | null>(readSession);
  // Ref mirror so getToken/refresh stay stable for the API-client bridge.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const applySession = useCallback((next: StoredSession | null) => {
    writeSession(next);
    setSession(next);
  }, []);

  const login = useCallback(
    async (username: string, password: string) => {
      const result = await postJson<LoginResponse>('/api/auth/login', { username, password });
      applySession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        user: result.user,
      });
    },
    [applySession],
  );

  const logout = useCallback(() => {
    applySession(null);
  }, [applySession]);

  const refresh = useCallback(async (): Promise<string | null> => {
    const current = sessionRef.current;
    if (!current) return null;
    try {
      const result = await postJson<LoginResponse>('/api/auth/refresh', {
        refreshToken: current.refreshToken,
      });
      applySession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        user: result.user,
      });
      return result.accessToken;
    } catch {
      applySession(null);
      return null;
    }
  }, [applySession]);

  const getToken = useCallback(() => sessionRef.current?.accessToken ?? null, []);

  const value = useMemo<AuthState>(() => {
    if (DEV_BYPASS) {
      return {
        authenticated: true,
        user: { id: 'dev', username: 'dev', name: 'Dev Super Admin', email: null, role: 'super_admin' },
        getToken: () => null,
        login: async () => undefined,
        logout: () => undefined,
        refresh: async () => null,
      };
    }
    return { authenticated: session != null, user: session?.user ?? null, getToken, login, logout, refresh };
  }, [session, getToken, login, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Redirects unauthenticated visitors to /login (public routes mount outside this). */
export function RequireAuth({ children }: PropsWithChildren) {
  const { authenticated } = useAuth();
  const location = useLocation();
  if (!authenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
